import { Button, DatePicker, Form, Input, InputNumber, Modal, Popconfirm } from "antd";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import type { VolunteerSlot, VolunteerTask } from "../types";
import type { VolunteerStore } from "../store";
import { TaskFormModal } from "./TaskFormModal";

interface Props {
  store: VolunteerStore;
  task: VolunteerTask;
  slot?: VolunteerSlot;
  open: boolean;
  onClose: () => void;
}

export const SlotFormModal = ({ store, task, slot, open, onClose }: Props) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [editTaskOpen, setEditTaskOpen] = useState(false);

  useEffect(() => {
    if (open) {
      form.setFieldsValue({
        label: slot?.label ?? "",
        description: slot?.description ?? "",
        timeRange:
          slot?.startTime && slot?.endTime ? [dayjs(slot.startTime), dayjs(slot.endTime)] : undefined,
        capacity: slot?.capacity,
        order: slot?.order ?? 0,
      });
    }
  }, [open, slot, form]);

  const handleOk = async () => {
    const values = await form.validateFields();
    const [startTime, endTime] = values.timeRange ?? [];
    setSubmitting(true);
    try {
      const data = {
        label: values.label || undefined,
        description: values.description || undefined,
        startTime: startTime?.toDate(),
        endTime: endTime?.toDate(),
        capacity: values.capacity,
        order: values.order,
      };
      if (slot?.objectId) {
        await store.updateSlot(slot.objectId, data);
      } else {
        await store.createSlot({ taskId: task.objectId!, ...data });
      }
      form.resetFields();
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!slot?.objectId) return;
    await store.deleteSlot(slot.objectId);
    onClose();
  };

  return (
    <>
      <Modal
        open={open}
        title={slot ? "Schicht bearbeiten" : "Neue Schicht"}
        okText={slot ? "Speichern" : "Anlegen"}
        cancelText="Abbrechen"
        confirmLoading={submitting}
        onOk={handleOk}
        onCancel={() => {
          form.resetFields();
          onClose();
        }}
        footer={(_, { OkBtn, CancelBtn }) => (
          <>
            {slot && (
              <Popconfirm
                title="Schicht wirklich löschen?"
                description="Alle Anmeldungen für diese Schicht werden ebenfalls gelöscht."
                onConfirm={handleDelete}
                okText="Ja"
                cancelText="Nein"
              >
                <Button danger style={{ float: "left" }}>
                  Löschen
                </Button>
              </Popconfirm>
            )}
            <Button style={{ float: "left" }} onClick={() => setEditTaskOpen(true)}>
              Aufgabe bearbeiten
            </Button>
            <CancelBtn />
            <OkBtn />
          </>
        )}
      >
        <Form form={form} layout="vertical" initialValues={{ order: 0 }}>
          <Form.Item
            name="label"
            label="Bezeichnung (optional)"
            extra="Wenn leer gelassen und eine Uhrzeit angegeben ist, wird automatisch die Uhrzeit (nur Stunden:Minuten) als Bezeichnung verwendet."
          >
            <Input placeholder="z.B. Frühschicht" />
          </Form.Item>
          <Form.Item
            name="description"
            label="Beschreibung (optional)"
            extra="Für Details zu genau dieser Schicht, z.B. wenn mehrere kleine Slots zur selben Aufgabe gehören."
          >
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="timeRange" label="Zeitraum (optional)">
            <DatePicker.RangePicker showTime format="DD.MM.YYYY HH:mm" style={{ width: "100%" }} minDate={dayjs("2026-09-17", "YYYY-MM-DD")} maxDate={dayjs("2026-09-20", "YYYY-MM-DD")}/>
          </Form.Item>
          <Form.Item
            name="capacity"
            label="Max. Anzahl Helfer:innen (leer = unbegrenzt)"
          >
            <InputNumber min={1} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="order" label="Reihenfolge">
            <InputNumber style={{ width: "100%" }} />
          </Form.Item>
        </Form>
      </Modal>
      <TaskFormModal store={store} task={task} open={editTaskOpen} onClose={() => setEditTaskOpen(false)} />
    </>
  );
};
